import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import NotaryCard from './components/NotaryCard';
import AppointmentModal from './components/AppointmentModal';
import FilterPanel from './components/FilterPanel';
import { notaryService } from '../../services/notaryService';
import EmptyState from '../../components/ui/EmptyState';
import { Shield, Calendar, MapPin, AlertCircle, RefreshCw, UserX } from 'lucide-react';

const NotaryAppointmentBookingSystem = () => {
  const [notaries, setNotaries] = useState([]);
  const [filteredNotaries, setFilteredNotaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNotary, setSelectedNotary] = useState(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [filters, setFilters] = useState({
    specialization: '',
    location: '',
    availability: ''
  });

  useEffect(() => {
    loadNotaries();
    const interval = setInterval(() => {
      if (!loading) {
        loadNotaries();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    filterNotaries();
  }, [notaries, filters]);

  const loadNotaries = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await notaryService?.getAllNotaries();

      if (response?.error) {
        setError(response?.error);
        setNotaries([]);
      } else {
        setNotaries(response?.data || []);
        setLastUpdateTime(new Date());
      }
    } catch (err) {
      setError('Erreur lors du chargement des notaires. Vérifiez votre connexion et réessayez.');
      setNotaries([]);
    } finally {
      setLoading(false);
    }
  };

  const filterNotaries = () => {
    let filtered = [...notaries];

    if (filters?.specialization) {
      filtered = filtered?.filter((notary) =>
        notary?.specializations?.includes(filters?.specialization)
      );
    }

    if (filters?.location) {
      filtered = filtered?.filter((notary) =>
        notary?.office_address?.toLowerCase()?.includes(filters?.location?.toLowerCase())
      );
    }

    setFilteredNotaries(filtered);
  };

  const handleBookAppointment = (notary) => {
    setSelectedNotary(notary);
    setShowAppointmentModal(true);
  };

  const handleAppointmentSuccess = () => {
    setShowAppointmentModal(false);
    setSelectedNotary(null);
  };

  const specializations = [
    'Real Estate',
    'Property Transfer',
    'Inheritance',
    'Business Law',
    'Family Law',
    'Contract Law'
  ];

  if (loading && !notaries?.length) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center border-none outline-none">
        <Header className="bg-transparent border-none absolute top-0 w-full" />
        <div className="w-16 h-16 rounded-2xl bg-brand-gold/10 flex items-center justify-center mb-6 shadow-lg shadow-brand-gold/20">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-gold border-t-transparent"></div>
        </div>
        <h2 className="text-xl font-heading font-bold mb-2">Chargement des notaires...</h2>
        <p className="text-white/40 text-sm font-light">Connexion sécurisée à la chambre des notaires</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white border-none outline-none flex flex-col relative overflow-x-hidden">
      
      {/* Halo lumineux d'ambiance */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-gold/5 rounded-full blur-[120px] -translate-y-1/4 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-600/5 rounded-full blur-[150px] translate-y-1/3 -translate-x-1/4" />
      </div>

      <Header className="bg-[#050505]/80 backdrop-blur-xl border-b border-white/5 sticky top-0 z-50" />
      
      {/* ══ HERO SECTION ══ */}
      <section className="relative z-10 pt-20 pb-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold text-white mb-6 tracking-tight leading-tight">
            Consultez un Notaire <br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-gold via-yellow-200 to-brand-gold">
              Agréé & Certifié
            </span>
          </h1>
          
          <p className="text-lg text-white/50 mb-10 font-light max-w-2xl mx-auto">
            Trouvez le notaire expert qui vous accompagnera dans vos transactions immobilières et sécurisera vos démarches juridiques.
          </p>
          
          {/* Badge Dynamique */}
          <div className="inline-flex flex-col items-center">
            <div className="bg-white/5 backdrop-blur-md rounded-2xl px-6 py-4 mb-8 shadow-xl inline-block border-none">
              <p className="text-xl font-heading font-bold text-white mb-1">
                {notaries?.length || 0} Notaire{notaries?.length !== 1 ? 's' : ''} Disponible{notaries?.length !== 1 ? 's' : ''}
              </p>
              {lastUpdateTime &&
                <div className="flex items-center justify-center gap-2 text-emerald-400/80 text-xs font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Mise à jour: {lastUpdateTime?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </div>
              }
            </div>

            {/* Quick Features */}
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-white/40 font-medium">
              <div className="flex items-center gap-2 bg-white/[0.02] px-4 py-2 rounded-full">
                <Shield size={16} className="text-brand-gold" />
                <span>Notaires certifiés</span>
              </div>
              <div className="flex items-center gap-2 bg-white/[0.02] px-4 py-2 rounded-full">
                <Calendar size={16} className="text-brand-gold" />
                <span>Rendez-vous en ligne</span>
              </div>
              <div className="flex items-center gap-2 bg-white/[0.02] px-4 py-2 rounded-full">
                <MapPin size={16} className="text-brand-gold" />
                <span>Partout en Côte d'Ivoire</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ MAIN CONTENT ══ */}
      <main className="flex-grow max-w-7xl mx-auto px-6 py-12 w-full relative z-10">
        
        {/* Error State */}
        {error &&
          <div className="mb-10 p-5 bg-red-500/10 rounded-2xl flex items-start gap-4 shadow-lg border-none">
            <div className="w-10 h-10 bg-red-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <AlertCircle size={20} className="text-red-400" />
            </div>
            <div>
              <p className="font-medium text-white mb-1">{error}</p>
              <button
                onClick={loadNotaries}
                className="text-red-400 hover:text-red-300 text-sm font-bold transition-colors underline decoration-red-400/30 underline-offset-4"
              >
                Réessayer maintenant
              </button>
            </div>
          </div>
        }

        <div className="grid lg:grid-cols-12 gap-10">
          
          {/* Sidebar: Filtres */}
          <aside className="lg:col-span-3">
            <div className="sticky top-28 bg-[#111111] rounded-3xl p-6 shadow-2xl border-none">
              <FilterPanel
                filters={filters}
                onFiltersChange={setFilters}
                specializations={specializations} 
              />
            </div>
          </aside>

          {/* Main List */}
          <div className="lg:col-span-9">
            
            {/* List Header */}
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
              <div>
                <h2 className="text-2xl font-heading font-bold text-white tracking-tight">
                  Notaires Disponibles
                </h2>
                <p className="text-sm text-white/40 mt-1 font-light">
                  {filteredNotaries?.length} notaire{filteredNotaries?.length !== 1 ? 's' : ''} trouvé{filteredNotaries?.length !== 1 ? 's' : ''}
                  {filters?.specialization || filters?.location ? ' correspondants à vos filtres' : ''}
                </p>
              </div>
              
              <div className="flex items-center gap-4">
                {loading && notaries?.length > 0 &&
                  <div className="flex items-center gap-2 text-brand-gold bg-brand-gold/10 px-3 py-1.5 rounded-full">
                    <div className="animate-spin rounded-full h-3 w-3 border-2 border-brand-gold border-t-transparent"></div>
                    <span className="text-xs font-bold uppercase tracking-widest">Actu...</span>
                  </div>
                }
                <button
                  onClick={loadNotaries}
                  disabled={loading}
                  className="flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white px-4 py-2 rounded-full text-sm font-medium transition-colors border-none"
                >
                  <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                  Actualiser
                </button>
              </div>
            </div>

            {/* Empty State */}
            {filteredNotaries?.length === 0 && !loading ?
              <div className="py-12 bg-[#111111] rounded-3xl text-center shadow-xl border-none">
                <div className="w-20 h-20 bg-white/5 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <UserX size={32} className="text-white/20" />
                </div>
                <h3 className="text-xl font-heading font-bold text-white mb-2">
                  {notaries?.length === 0 ? 'Aucun notaire enregistré' : 'Aucun notaire trouvé'}
                </h3>
                <p className="text-white/40 text-sm max-w-md mx-auto mb-6 font-light">
                  {notaries?.length === 0
                    ? "Aucun notaire n'a été ajouté au système pour le moment." 
                    : "Essayez de modifier vos critères de recherche pour voir plus de résultats."}
                </p>
                <button
                  onClick={loadNotaries}
                  className="bg-brand-gold text-black hover:bg-yellow-400 font-bold px-6 py-3 rounded-full transition-colors border-none inline-flex items-center gap-2"
                >
                  <RefreshCw size={16} />
                  Actualiser la liste
                </button>
              </div> 
            :
              <>
                {/* Notary Grid */}
                <div className="grid md:grid-cols-2 gap-6 mb-12">
                  {filteredNotaries?.map((notary) =>
                    <div key={notary?.id} className="bg-[#111111] rounded-3xl overflow-hidden shadow-xl hover:-translate-y-1 transition-all duration-300 border-none group">
                      {/* 
                        Note: Assurez-vous que le composant <NotaryCard /> n'a pas de bordures dures en dur.
                        S'il a un bg, il vaut mieux qu'il soit transparent pour hériter de ce bg-[#111111].
                      */}
                      <NotaryCard
                        notary={notary}
                        onBookAppointment={() => handleBookAppointment(notary)} 
                      />
                    </div>
                  )}
                </div>

                {/* Stats Summary (Apple Style Bento) */}
                {filteredNotaries?.length > 0 &&
                  <div className="bg-[#111111] rounded-3xl p-8 shadow-2xl border-none">
                    <h3 className="text-lg font-heading font-bold text-white mb-6 tracking-tight">
                      Résumé du réseau
                    </h3>
                    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5">
                      <div className="bg-white/5 rounded-2xl p-6 flex flex-col justify-center border-none">
                        <p className="text-3xl font-heading font-bold text-emerald-400 mb-1">
                          {filteredNotaries?.filter((n) => n?.is_verified)?.length}
                        </p>
                        <p className="text-xs font-bold uppercase tracking-widest text-white/30">Certifiés</p>
                      </div>
                      <div className="bg-white/5 rounded-2xl p-6 flex flex-col justify-center border-none">
                        <p className="text-3xl font-heading font-bold text-brand-gold mb-1">
                          {filteredNotaries?.filter((n) => n?.specializations?.length > 0)?.length}
                        </p>
                        <p className="text-xs font-bold uppercase tracking-widest text-white/30">Spécialisés</p>
                      </div>
                      <div className="sm:col-span-2 md:col-span-1 bg-white/5 rounded-2xl p-6 flex flex-col justify-center border-none">
                        <p className="text-3xl font-heading font-bold text-white mb-1">
                          {filteredNotaries?.length}
                        </p>
                        <p className="text-xs font-bold uppercase tracking-widest text-white/30">Total Disponibles</p>
                      </div>
                    </div>
                  </div>
                }
              </>
            }
          </div>
        </div>
      </main>

      {/* Appointment Modal */}
      {showAppointmentModal && selectedNotary &&
        <AppointmentModal
          notary={selectedNotary}
          onClose={() => setShowAppointmentModal(false)}
          onSuccess={handleAppointmentSuccess} 
        />
      }
    </div>
  );
};

export default NotaryAppointmentBookingSystem;